import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from '~/Providers/Moment';
import Button from '~/Components/Global/Form/Button';

import './CustomTimePicker.scss';

const CustomTimePicker = forwardRef(
  (
    {
      onSave,
      onBack,
      initialTime = moment()
        .hour(12)
        .minute(30),
      showHeaderAndFooter = true,
    },
    ref
  ) => {
    const userOptions = useSelector(state => state.UserOptions.data);
    const use_intl_time = userOptions?.use_intl_time || false;

    const hours = use_intl_time
      ? Array.from({ length: 24 }, (_, i) => i)
      : Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));
    const periods = use_intl_time ? [] : ['am', 'pm'];

    const [selectedHour, setSelectedHour] = useState(
      use_intl_time ? initialTime.format('H') : initialTime.format('h')
    );
    const [selectedMinute, setSelectedMinute] = useState(initialTime.format('mm'));
    const [selectedPeriod, setSelectedPeriod] = useState(
      use_intl_time ? null : initialTime.format('a')
    );

    const [scrollMomentum, setScrollMomentum] = useState({
      hour: 0,
      minute: 0,
      period: 0,
    });

    const timePickerRef = useRef(null);

    useEffect(() => {
      const handleEvent = e => {
        e.stopPropagation();
      };

      const element = timePickerRef.current;
      if (element) {
        element.addEventListener('click', handleEvent);
        element.addEventListener('mousedown', handleEvent);
        element.addEventListener('touchstart', handleEvent);

        return () => {
          element.removeEventListener('click', handleEvent);
          element.removeEventListener('mousedown', handleEvent);
          element.removeEventListener('touchstart', handleEvent);
        };
      }
    }, []);

    const getVisibleItems = (items, selectedIndex, type) => {
      if (type === 'period') {
        return [
          {
            value: 'am',
            index: 0,
            isSelected: selectedPeriod === 'am',
          },
          {
            value: 'pm',
            index: 1,
            isSelected: selectedPeriod === 'pm',
          },
        ];
      }

      const totalItems = items.length;
      const visibleItems = [];

      for (let i = -2; i <= 2; i++) {
        let index = (selectedIndex + i + totalItems) % totalItems;
        visibleItems.push({
          value: items[index],
          index: index,
          isSelected: i === 0,
        });
      }

      return visibleItems;
    };

    const visibleHours = getVisibleItems(hours, hours.indexOf(parseInt(selectedHour)), 'hour');
    const visibleMinutes = getVisibleItems(minutes, minutes.indexOf(selectedMinute), 'minute');
    const visiblePeriods = getVisibleItems(periods, periods.indexOf(selectedPeriod), 'period');

    const handleScroll = (direction, type) => {
      if (type === 'period') {
        if (direction < 0) {
          setSelectedPeriod('am');
        } else {
          setSelectedPeriod('pm');
        }
        return;
      }

      if (type === 'hour') {
        const currentIndex = use_intl_time
          ? hours.indexOf(parseInt(selectedHour))
          : hours.indexOf(parseInt(selectedHour));
        const newIndex = (currentIndex + direction + hours.length) % hours.length;
        setSelectedHour(hours[newIndex].toString());
      } else if (type === 'minute') {
        const currentIndex = minutes.indexOf(selectedMinute);
        const newIndex = (currentIndex + direction + minutes.length) % minutes.length;
        setSelectedMinute(minutes[newIndex]);
      }
    };

    const handleWheel = (event, type) => {
      event.preventDefault?.();
      const deltaY = event.deltaY;
      const SCROLL_THRESHOLD = 30;

      setScrollMomentum(prev => {
        const newMomentum = { ...prev };
        newMomentum[type] += Math.abs(deltaY);

        if (newMomentum[type] >= SCROLL_THRESHOLD) {
          const direction = deltaY > 0 ? 1 : -1;
          handleScroll(direction, type);
          newMomentum[type] = 0;
        }

        return newMomentum;
      });
    };

    // Keyboard and wheel both step through handleScroll with the same direction convention
    // (ArrowDown / scroll down = +1), so every value reachable by one is reachable by the other.
    const KEY_DIRECTIONS = { ArrowUp: -1, ArrowDown: 1 };
    // Left/Right move between columns like a native <input type="time">. This is in addition to
    // Tab, which still moves focus through the columns and out of the picker.
    const COLUMN_STEPS = { ArrowLeft: -1, ArrowRight: 1 };

    const focusAdjacentColumn = (column, step) => {
      const columns = Array.from(timePickerRef.current?.querySelectorAll('.time-column') || []);
      columns[columns.indexOf(column) + step]?.focus();
    };

    const handleKeyDown = (event, type) => {
      if (COLUMN_STEPS[event.key]) {
        event.preventDefault();
        focusAdjacentColumn(event.currentTarget, COLUMN_STEPS[event.key]);
        return;
      }
      const direction = KEY_DIRECTIONS[event.key];
      if (!direction) return;
      event.preventDefault();
      handleScroll(direction, type);
    };

    const getColumnSpinbutton = type => {
      if (type === 'period') {
        return {
          label: 'AM or PM',
          min: 0,
          max: 1,
          now: selectedPeriod === 'pm' ? 1 : 0,
          text: selectedPeriod?.toUpperCase(),
        };
      }
      if (type === 'hour') {
        return {
          label: 'Hour',
          min: hours[0],
          max: hours[hours.length - 1],
          now: parseInt(selectedHour),
        };
      }
      return { label: 'Minute', min: 0, max: 59, now: parseInt(selectedMinute) };
    };

    const getCurrentTime = () => {
      let hour = parseInt(selectedHour);

      if (use_intl_time) {
        return moment()
          .hour(hour)
          .minute(parseInt(selectedMinute));
      } else {
        if (selectedPeriod === 'pm' && hour !== 12) {
          hour += 12;
        } else if (selectedPeriod === 'am' && hour === 12) {
          hour = 0;
        }

        return moment()
          .hour(hour)
          .minute(parseInt(selectedMinute));
      }
    };

    const handleSave = () => {
      const customTime = getCurrentTime();
      const displayTime = customTime.format(use_intl_time ? 'HH:mm' : 'h:mm a');
      onSave(customTime, displayTime);
    };

    useImperativeHandle(ref, () => ({
      handleSave,
    }));

    const renderColumn = (items, type) => {
      const spinbutton = getColumnSpinbutton(type);
      return (
        <div
          className={`time-column ${type === 'period' ? 'time-column--period' : ''}`}
          role="spinbutton"
          tabIndex={0}
          aria-label={spinbutton.label}
          aria-valuemin={spinbutton.min}
          aria-valuemax={spinbutton.max}
          aria-valuenow={spinbutton.now}
          aria-valuetext={spinbutton.text}
          onWheel={e => handleWheel(e, type)}
          onKeyDown={e => handleKeyDown(e, type)}
        >
          <div className="time-items">
            {items.map((item, index) => (
              <div
                key={`${item.value}-${index}`}
                className={`time-item ${item.isSelected ? 'selected' : ''}`}
              >
                {item.value}
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <div ref={timePickerRef} className="custom-time-picker">
        {showHeaderAndFooter && (
          <div className="custom-time-picker__header">
            <button className="back-button" onClick={onBack} aria-label="Go back to alarm options">
              <span className="material-icons-outlined back-button-icon">chevron_left</span>
            </button>
            <span>Custom</span>
          </div>
        )}

        <div className="custom-time-picker__body">
          <div className="custom-time-picker__time-section">
            <div className="time-picker-container">
              <div className="selected-row-background" />
              {renderColumn(visibleHours, 'hour')}
              <div className="time-separator">:</div>
              {renderColumn(visibleMinutes, 'minute')}
              {!use_intl_time && renderColumn(visiblePeriods, 'period')}
            </div>
          </div>
        </div>

        {showHeaderAndFooter && (
          <div className="custom-time-picker__footer">
            <Button
              text="SAVE CUSTOM TIME"
              className="btn btn-default save-custom-time-button"
              onClick={handleSave}
            />
          </div>
        )}
      </div>
    );
  }
);

CustomTimePicker.propTypes = {
  onSave: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  initialTime: PropTypes.object,
  showHeaderAndFooter: PropTypes.bool,
};

export default CustomTimePicker;
